import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import AdminPanel from './components/AdminPanel';
import Consultas from './components/Consultas';
import ConsultaCpf from './components/ConsultaCpf';
import ConsultaCpfTemCnpj from './components/ConsultaCpfTemCnpj';
import LimitesConsultas from './components/LimitesConsultas';
import LogoDisplay from './components/LogoDisplay';
import { useAutoLogout } from './hooks/useAutoLogout';
import axios from 'axios';

// Configurar axios para incluir token nas requisições
// Usar caminho relativo já que frontend e backend estão na mesma porta
axios.defaults.baseURL = '/api';

// Interceptor para adicionar token
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratar erros de autenticação
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Só redirecionar para login se o erro 401 for de uma requisição do próprio backend
    // Verificar se a URL é relativa (começa com /) ou usa o baseURL
    const requestUrl = error.config?.url || '';
    const baseURL = error.config?.baseURL || axios.defaults.baseURL || '/api';
    const fullUrl = error.config?.fullURL || requestUrl;
    
    // Verificar se é uma requisição interna (não externa)
    const isInternalRequest = requestUrl.startsWith('/') || 
                              requestUrl.startsWith(baseURL) ||
                              (!requestUrl.startsWith('http://') && !requestUrl.startsWith('https://')) ||
                              (fullUrl.includes(window.location.hostname) && 
                               !fullUrl.includes('cep.melhorplano.net') && 
                               !fullUrl.includes('melhorplano.net') &&
                               !fullUrl.includes('internet.tim.com.br') &&
                               !fullUrl.includes('85.239.241.196'));
    
    // Não redirecionar para login se erro 401 vier da rota melhorplano (backend vai tratar com proxy)
    const isMelhorPlanoRequest = requestUrl.includes('/consultas/melhorplano') || 
                                  requestUrl.includes('melhorplano');
    
    if (error.response?.status === 401 && isInternalRequest && !isMelhorPlanoRequest) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('logo_path');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

function PrivateRoute({ children, requireAdmin = false, requireAdminOrProprietario = false }) {
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  if (requireAdminOrProprietario && user?.role !== 'admin' && user?.role !== 'proprietario') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  const [user, setUser] = useState(null);
  const [logoPath, setLogoPath] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedLogo = localStorage.getItem('logo_path');
    if (storedUser) {
      const userObj = JSON.parse(storedUser);
      setUser(userObj);
      // Logo pode vir do user object ou do localStorage
      setLogoPath(userObj.logo_path || storedLogo);
    }
  }, []);

  // Atualizar logoPath quando user mudar
  useEffect(() => {
    if (user && user.logo_path) {
      setLogoPath(user.logo_path);
    } else if (user) {
      // Se não tem logo_path no user, buscar do localStorage
      const storedLogo = localStorage.getItem('logo_path');
      setLogoPath(storedLogo);
    }
  }, [user]);

  const handleLogout = async () => {
    try {
      // Chamar rota de logout no backend para invalidar sessão
      const token = localStorage.getItem('token');
      if (token) {
        await axios.post('/auth/logout');
      }
    } catch (err) {
      // Se falhar ao chamar logout, ainda assim limpar localStorage (garantir logout local)
      console.error('Erro ao fazer logout no servidor:', err);
    } finally {
      // Sempre limpar localStorage mesmo se a chamada ao backend falhar
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('logo_path');
      setUser(null);
      setLogoPath(null);
      navigate('/login', { replace: true });
    }
  };

  // Hook para logout automático após 30 minutos de inatividade
  useAutoLogout(30, handleLogout);

  return (
    <div className="app-container">
      {user && (
        <header className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <LogoDisplay logoPath={logoPath} />
            <h1>Sistema de Consultas</h1>
          </div>
          <div className="user-info">
            <span>Usuário: {user.username}</span>
            <span>|</span>
            <span>Role: {user.role}</span>
            <button className="logout-btn" onClick={handleLogout}>
              Sair
            </button>
          </div>
        </header>
      )}
      <main className="main-content">
        <Routes>
          <Route
            path="/login"
            element={
              user ? (
                <Navigate to={user.role === 'admin' || user.role === 'proprietario' ? '/admin' : '/dashboard'} replace />
              ) : (
                <Login setUser={setUser} />
              )
            }
          />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Dashboard user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <PrivateRoute requireAdminOrProprietario={true}>
                <AdminPanel />
              </PrivateRoute>
            }
          />
          <Route
            path="/consultas"
            element={
              <PrivateRoute>
                <Consultas />
              </PrivateRoute>
            }
          />
          <Route
            path="/consulta-cpf"
            element={
              <PrivateRoute>
                <ConsultaCpf />
              </PrivateRoute>
            }
          />
          <Route
            path="/consulta-cpf-tem-cnpj"
            element={
              <PrivateRoute>
                <ConsultaCpfTemCnpj />
              </PrivateRoute>
            }
          />
          <Route
            path="/limites-consultas"
            element={
              <PrivateRoute requireAdminOrProprietario={true}>
                <LimitesConsultas />
              </PrivateRoute>
            }
          />
          <Route path="/" element={<Navigate to="/login" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}

export default App;
